import * as Sentry from '@sentry/react';
import { useDebounceValue } from 'usehooks-ts';
import { Route, Routes, useLocation } from 'react-router-dom';
import { lazy, useState, useEffect, useCallback } from 'react';
import {
  Utils,
  Dialog,
  NotFound,
  useAlert,
  Suspense,
  DialogType,
  ProtectedRoute,
  StandardButton,
  HeaderContextProvider,
} from '@gv/triage-components';

import { Home } from 'pages';
import { AppRoutes } from 'config';
import { Header } from 'components';
import { getBrowserState } from 'utils';
import { SidebarContextProvider } from 'context';
import { useAppDispatch, useAppSelector } from 'store';
import { Login, SignUp, ResetPassword, ForgotPassword } from 'pages/auth';
import {
  setAuthPath,
  setAuthError,
  selectAuthUser,
  selectAuthError,
  selectIsLoggedIn,
} from 'store/slices/auth';

import * as Styles from './styles';
import { AppContainer } from './app-container';
import { useLoggerHook } from './useLoggerHook';
import { Component, ComponentWithOptions } from './types';

const AppPrivate = lazy(() => import('./app-private'));

function isComponentWithOptions(
  value: Component | ComponentWithOptions
): value is ComponentWithOptions {
  return (value as ComponentWithOptions).allowedForLoggedIn !== undefined;
}
const mappedRoutes = {
  [AppRoutes.Home]: Home,
  [AppRoutes.Auth.Login]: Login,
  [AppRoutes.Auth.SignUp]: SignUp,
  [AppRoutes.Auth.ForgotPassword]: ForgotPassword,
  [AppRoutes.Auth.ResetPassword]: {
    allowedForLoggedIn: true,
    Component: ResetPassword,
  },
};

const homeRoute = AppRoutes.Home;
const savePathOnAuthRedirect = true;

export const App = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [browserState] = useState(getBrowserState());

  const { showErrorAlert } = useAlert();
  const dispatch = useAppDispatch();

  useLoggerHook();

  const user = useAppSelector(selectAuthUser);
  const authorizedId = user?.id ? String(user.id) : undefined;

  const authError = useAppSelector(selectAuthError);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [errorDebounce] = useDebounceValue(error, 1000);
  const location = useLocation();

  useEffect(() => {
    if (authError) {
      setError(Utils.Error.getErrorMessage(authError));
      dispatch(setAuthError(undefined));
    }
  }, [authError]);

  const savePath = useCallback(() => {
    dispatch(setAuthPath({ path: location.pathname + location.search }));
  }, [dispatch, location.pathname, location.search]);

  useEffect(() => {
    if (errorDebounce) {
      showErrorAlert(errorDebounce);
      setError(undefined);
    }
  }, [errorDebounce]);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: authorizedId,
      email: authorizedId,
      username: user.name,
    });
  }, [authorizedId, user?.name]);

  const isNotFoundPage = location.pathname === AppRoutes.NotFound;

  return (
    <AppContainer>
      {!isNotFoundPage && <Header />}
      {/* {!isLoggedIn && <OutdatedBanner polling />} */}
      <Styles.RouteContainer>
        <Routes>
          <Route
            path={AppRoutes.NotFound}
            element={
              <Suspense>
                <NotFound fitParentWidth homePath={homeRoute} />
              </Suspense>
            }
          />
          {Object.keys(mappedRoutes).map((path) => {
            const route = mappedRoutes[path];
            const hasOptions = isComponentWithOptions(route);
            const Page = hasOptions ? route.Component : route;
            return (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute
                    redirect={homeRoute}
                    allowed={
                      hasOptions ? route.allowedForLoggedIn : !isLoggedIn
                    }
                  >
                    <Suspense>
                      <Page />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            );
          })}
          <Route
            path="*"
            element={
              <ProtectedRoute
                allowed={isLoggedIn}
                redirect={AppRoutes.Auth.Login}
                onWillRedirect={savePathOnAuthRedirect ? savePath : undefined}
              >
                <Suspense>
                  <SidebarContextProvider>
                    <HeaderContextProvider>
                      <AppPrivate />
                    </HeaderContextProvider>
                  </SidebarContextProvider>
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Styles.RouteContainer>

      {browserState.outdated && (
        <Dialog
          open
          type={DialogType.Plain}
          title="Update your browser"
          closeOnDocumentClick={false}
          text={
            <>
              <p>
                In order to ensure the correct operation of the platform update
                your browser to the latest version.
              </p>
              <p>
                To find out how to update your browser, please open the link
                below and follow the instructions.
              </p>
            </>
          }
          buttons={() => (
            <div style={{ width: '100%' }}>
              <StandardButton
                text="How to update web browser"
                onClick={() =>
                  window.open(
                    'https://www.wikihow.com/Update-Your-Browser',
                    '_blank'
                  )
                }
              />
            </div>
          )}
        />
      )}
    </AppContainer>
  );
};
